import { useEffect, useState } from 'react';
import { sessionsApi } from '../../../../api/sessionsApi';
import { DEFAULT_PROVIDER, TRANSCRIPT_PROVIDERS } from '../../../../constants/transcriptProviders';
import {
  DEFAULT_TRANSCRIPTION_LANGUAGE,
  getTranscriptionLanguageName,
} from '../../../../constants/transcriptionLanguages';

const providerLabel = (value) => TRANSCRIPT_PROVIDERS.find((p) => p.value === value)?.label || value;

/** Starting a first transcript, or replacing the current one, with a chosen provider and language. */
export function useTranscriptGeneration(id, session, notify, reloadSession) {
  const [provider, setProvider] = useState(DEFAULT_PROVIDER);
  const [language, setLanguage] = useState(DEFAULT_TRANSCRIPTION_LANGUAGE);
  const [isStarting, setIsStarting] = useState(false);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);

  useEffect(() => {
    if (session?.transcript_language) setLanguage(session.transcript_language);
  }, [session?.transcript_language]);

  const run = async ({ replace }) => {
    setIsStarting(true);
    try {
      if (replace) await sessionsApi.regenerateTranscript(id);
      await sessionsApi.startTranscript(id, provider, language);
      notify({
        type: 'success',
        title: replace ? 'Transcript regeneration started' : 'Transcript generation started',
        message: `${providerLabel(provider)} · ${getTranscriptionLanguageName(language)}`,
      });
      setIsReplaceOpen(false);
      setTimeout(reloadSession, 1500);
    } catch (e) {
      notify({
        type: 'error',
        title: replace ? 'Failed to regenerate transcript' : 'Failed to start transcript',
        message: e.message,
      });
    } finally {
      setIsStarting(false);
    }
  };

  const openReplace = () => {
    if (TRANSCRIPT_PROVIDERS.some((p) => p.value === session?.transcript_provider)) {
      setProvider(session.transcript_provider);
    }
    setIsReplaceOpen(true);
  };

  return {
    provider,
    setProvider,
    language,
    setLanguage,
    isStarting,
    start: () => run({ replace: false }),
    isReplaceOpen,
    openReplace,
    closeReplace: () => setIsReplaceOpen(false),
    confirmReplace: () => run({ replace: true }),
  };
}
